import { Component, computed, inject, output, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { ProgressBarModule } from '@openng/optimus-ui/progressbar';
import type { BackgroundTaskObject } from '@it2go/types';
import {
    BackgroundTaskResultType,
    BackgroundTaskStatus,
} from '@libs/types/src/notifications/enum/background-task.enum';
import { ButtonComponent } from '../../../../components/kit/button/button.component';
import { IconComponent } from '../../../../components/kit/icon/icon.component';
import { LocalDatePipe } from '../../../shared/pipes/local-date.pipe';
import { BackgroundTaskStore } from '../../store/background-tasks/background-task.store';
import { BackgroundTaskParamsPipe } from '../../store/background-tasks/background-task-params.pipe';

@Component({
    selector: 'app-background-task-progress',
    templateUrl: './background-task-progress.component.html',
    styleUrl: './background-task-progress.component.scss',
    imports: [
        TranslatePipe,
        ProgressBarModule,
        ButtonComponent,
        IconComponent,
        LocalDatePipe,
        BackgroundTaskParamsPipe,
    ],
})
export class BackgroundTaskProgressComponent {
    protected readonly store = inject(BackgroundTaskStore);
    protected readonly downloadingIds = signal(new Set<string>());
    protected readonly visibleTasks = computed(() =>
        this.store.bubbleTasks().slice(0, 3),
    );
    protected readonly remainingCount = computed(() =>
        Math.max(this.store.bubbleTasks().length - 3, 0),
    );

    public readonly openTasks = output<void>();

    protected async openResult(task: BackgroundTaskObject): Promise<void> {
        this.downloadingIds.update((ids) => new Set(ids).add(task.id));
        try {
            const result = await this.store.openResult(task);
            if (result.url && !result.expired) {
                this.store.dismissBubble(task.id);
            }
        } finally {
            this.downloadingIds.update((ids) => {
                const next = new Set(ids);
                next.delete(task.id);
                return next;
            });
        }
    }

    protected resultAction(task: BackgroundTaskObject): string {
        return task.resultType === BackgroundTaskResultType.PRINT
            ? 'layout.backgroundTasks.actions.open'
            : 'layout.backgroundTasks.actions.download';
    }

    protected icon(task: BackgroundTaskObject): string {
        if (task.status === BackgroundTaskStatus.COMPLETED) return 'pi-check';
        if (task.status === BackgroundTaskStatus.FAILED) return 'pi-times';
        if (task.status === BackgroundTaskStatus.EXPIRED) return 'pi-clock';
        return task.type.includes('import') ? 'pi-upload' : 'pi-download';
    }

    protected readonly BackgroundTaskStatus = BackgroundTaskStatus;
    protected readonly BackgroundTaskResultType = BackgroundTaskResultType;
}
